/** Shared badge tier + grouping helpers (web dashboard + profile). */

export function parseNameAndTier(name, fallbackTier) {
  const m = (name || "").match(/\s*\((Bronze|Silver|Gold)\)\s*$/i);
  const cleanName = (name || "").replace(/\s*\((Bronze|Silver|Gold)\)\s*$/i, "").trim();
  const tier = (m ? m[1] : fallbackTier || "bronze").toLowerCase();
  return { cleanName, tier };
}

export const TIER = {
  bronze: { color: "#8E5625", tint: "rgb(255 246 239)", glow: "rgba(198,130,63,.22)", order: 1, next: "silver" },
  silver: { color: "#7E8DA0", tint: "rgb(247 249 252)", glow: "rgba(126,141,160,.22)", order: 2, next: "gold" },
  gold: { color: "#9B7A1C", tint: "rgb(255 249 231)", glow: "rgba(155,122,28,.22)", order: 3, next: null },
};

export const BADGE_COUNT_NOUNS = {
  civic_caller: { one: "call", other: "calls" },
  constituent_commentator: { one: "comment", other: "comments" },
  constituent_messenger: { one: "message", other: "messages" },
  letter_writer: { one: "letter", other: "letters" },
  community_participant: { one: "event", other: "events" },
  community_volunteer: { one: "volunteer hour", other: "volunteer hours" },
};

export function getBadgeCountLabel(series, count) {
  const noun = BADGE_COUNT_NOUNS[series];
  if (!noun || typeof count !== "number" || count <= 0) return null;
  return `${count} ${count === 1 ? noun.one : noun.other}`;
}

/** One row per series: highest earned tier + progress toward the next. */
export function processBadges(badges) {
  const seriesMap = {};

  badges.forEach((b) => {
    const { cleanName, tier } = parseNameAndTier(b.name, b.tier?.toLowerCase?.());
    const series = b.series;

    if (!seriesMap[series]) {
      seriesMap[series] = {
        series,
        cleanName,
        tiers: {},
        highestEarned: null,
        nextTier: null,
      };
    }

    seriesMap[series].tiers[tier] = { ...b, tier, cleanName };

    if (b.earned) {
      const currentOrder = TIER[tier]?.order || 0;
      const highestOrder = seriesMap[series].highestEarned
        ? (TIER[seriesMap[series].highestEarned]?.order || 0)
        : 0;
      if (currentOrder > highestOrder) {
        seriesMap[series].highestEarned = tier;
      }
    }
  });

  Object.values(seriesMap).forEach((s) => {
    if (s.highestEarned) {
      const nextTierKey = TIER[s.highestEarned]?.next;
      if (nextTierKey && s.tiers[nextTierKey]) {
        s.nextTier = s.tiers[nextTierKey];
      }
    } else if (s.tiers.bronze) {
      s.nextTier = s.tiers.bronze;
    }
  });

  return Object.values(seriesMap);
}

/** Profile view: only the top earned tier per series. */
export function getHighestEarnedBadges(badges) {
  return processBadges(badges)
    .filter((group) => group.highestEarned)
    .map((group) => group.tiers[group.highestEarned]);
}
